import { Component } from '@mycolorway/tao';
import $ from 'jquery';
import { throttle } from 'next/javascripts/modules/utils';
import { showMessage } from 'next/javascripts/components/dialog';
import eventListenerMixin from 'next/javascripts/mixins/event-listener-mixin';
// import CKEditorInspector from '@ckeditor/ckeditor5-inspector';
import defaultConfig from './default-config';

import 'next/stylesheets/components/editor/element.scss';

const PENDING_ACTIONS_REGEX = [
  /^正在上传\s?\d+/,
];

let editorPreloaded = false;

Component('tr-editor', {
  mixins: [eventListenerMixin],

  properties: {
    unsupported: {
      type: Boolean,
      default: false,
    },

    config: {
      type: Object,
      default: {},
    },

    lazyload: {
      type: Boolean,
      default: false,
    },

    focusOnReady: {
      type: Boolean,
      default: false,
    },

    decoupled: {
      type: Boolean,
      default: false,
    },

    scrollContainer: String,

    scrollContainerTopOffset: {
      type: Number,
      default: 0,
    },
  },

  connected() {
    this.source = this.querySelector('.editor-source');
    this.teditor = new Promise((resolve) => {
      Promise.all([
        import('./teditor'),
        import('./codemirror'),
      ]).then(([{ default: TEditor, TEditorDecoupled, Watchdog }]) => {
        resolve({ TEditor, TEditorDecoupled, Watchdog });
      });
    });

    if (this.lazyload) {
      this.preloadEditor();
    } else {
      this.initEditor();
    }
  },

  async initEditor() {
    if (this.unsupported || this.editorReady) return;

    const { TEditor, TEditorDecoupled, Watchdog } = await this.teditor;

    this.watchdog = new Watchdog();

    this.watchdog.setCreator((el, config) => {
      const Editor = this.decoupled ? TEditorDecoupled : TEditor;
      return Editor.create(el, config)
        .then((editor) => {
          // CKEditorInspector.attach(editor);
          this.editor = editor;
          this.classList.remove('loading');

          this.initScrollContainer();
          this.registerEditorHooks();

          this.editorReady = true;
          if (this.focusOnReady) this.focus();

          this.namespacedTrigger('editorReady', {
            detail: { editor },
            bubbles: true,
          });
          return editor;
        });
    });

    this.watchdog.setDestructor((editor) => {
      if (editor.state === 'destroyed') return;
      editor.destroy();
    });

    this.watchdog.create(
      this.source.querySelector('*:first-child') || this.source,
      this.initConfig(),
    );
  },

  async preloadEditor() {
    if (editorPreloaded) return;

    const tempSource = document.createElement('div');
    tempSource.style.display = 'none';
    document.body.appendChild(tempSource);

    const { TEditor } = await this.teditor;
    TEditor.create(tempSource, this.initConfig()).then((editor) => {
      editorPreloaded = true;
      editor.destroy();
      document.body.removeChild(tempSource);
    });
  },

  destroyEditor() {
    if (this.editor && this.editorReady) {
      this.editorReady = false;
      this.watchdog.destroy();
    }
  },

  registerEditorHooks() {
    this.addEditableViewFocusHook();
    this.addPendingActionsHook();
    this.addDataChangeHook();
    this.interceptDragEvents();
    this.addNotificationHook();
  },

  disconnected() {
    this.stopListening();
    this.destroyEditor();
  },

  focus() {
    if (this.editor) {
      const { model } = this.editor;

      model.change((writer) => {
        const root = model.document.getRoot();
        const { childCount } = root;

        if (childCount > 0) {
          writer.setSelection(root.getChild(childCount - 1), 'after');
        } else {
          writer.setSelection(root, root.maxOffset - 1);
        }
      });

      this.editor.editing.view.focus();
    }
  },

  getData(options) {
    return this.editor ? this.editor.getData(options) : null;
  },

  setData(data) {
    if (this.editor) this.editor.setData(data);
  },

  getAttachIds() {
    if (!this.editor) return [];

    const attachmentUploadPlugin = this.editor.plugins.get('AttachmentUpload');
    return attachmentUploadPlugin ? attachmentUploadPlugin.attachIds : [];
  },

  scrollToTheSelection({ viewportOffset = 20 } = {}) {
    const selection = this.getSelectionDomRange();
    if (!selection) return;

    import('./teditor').then(({ utils }) => {
      utils.scrollViewportToShowTarget({ target: selection, viewportOffset });
    });
  },

  getSelectionParentElement() {
    return new Promise((resolve) => {
      const selection = this.getSelectionDomRange();
      if (!selection) {
        resolve(null);
        return;
      }

      import('./teditor').then(({ utils }) => {
        let parent = selection.commonAncestorContainer;

        if (utils.isText(parent)) {
          parent = parent.parentNode;
        }

        resolve(parent);
      });
    });
  },

  getSelectionDomRange() {
    if (!this.editor) return null;

    const { document, domConverter } = this.editor.editing.view;
    if (!document.selection || document.isCodeEditing) return null;

    const range = document.selection.getFirstRange();
    return range ? domConverter.viewRangeToDom(range) : null;
  },

  addEditableViewFocusHook() {
    const { editor } = this;

    editor.listenTo(
      editor.ui.view.editable,
      'change:isFocused',
      (event, name, isFocused) => {
        this.namespacedTrigger(isFocused ? 'focus' : 'blur', { bubbles: true });
      },
    );
  },

  addPendingActionsHook() {
    const pendingActions = this.editor.plugins.get('PendingActions');
    if (!pendingActions) return;

    this.editor.listenTo(pendingActions, 'change:hasAny', () => {
      const messages = Array.from(pendingActions).filter(
        ({ message }) => PENDING_ACTIONS_REGEX.some(regex => regex.test(message)),
      ).map(({ message }) => message);

      this.namespacedTrigger('actionPending', {
        detail: { messages },
        bubbles: true,
      });
    });
  },

  addDataChangeHook() {
    const { editor } = this;
    const { document } = editor.model;

    editor.listenTo(document, 'change:data', () => {
      this.renderEditingViewForChanges = true;
      this.namespacedTrigger('change', { bubbles: true });
    });

    editor.listenTo(editor.editing.view, 'render', () => {
      if (this.renderEditingViewForChanges) {
        this.namespacedTrigger('changesRendered', { bubbles: true });
        this.renderEditingViewForChanges = false;
      }
    });
  },

  interceptDragEvents() {
    const { document } = this.editor.editing.view;

    // simple filedrop 会在 window.document 上响应 dragover 并改写 dropEffect,
    // 导致拖拽图片或文件到编辑器时不能被正常 drop，所以需阻止其往上冒泡
    this.editor.listenTo(document, 'dragover', (event, data) => {
      data.preventDefault();
      data.stopPropagation();
    });

    // ckeditor-image-upload 插件会阻止 drop 冒泡，
    // 拖拽图片或文件到编辑器后会导致页面上的 simple filedrop 未能隐藏掉 dropzone
    this.editor.listenTo(document, 'drop', () => {
      $('.simple-filedrop').each((idx, el) => {
        const filedrop = $(el).data('filedrop');
        if (filedrop) filedrop.hideDropzone();
      });
    });
  },

  addNotificationHook() {
    const notification = this.editor.plugins.get('Notification');
    if (!notification) return;

    this.editor.listenTo(notification, 'show:warning', (event, data) => {
      event.stop();
      showMessage({
        title: data.title,
        content: data.message,
      });
    }, { priority: 'high' });
  },

  initScrollContainer() {
    if (!this.scrollContainer) return;

    const container = document.querySelector(this.scrollContainer);
    if (!container) return;

    const { stickyPanel } = this.editor.ui.view;
    stickyPanel.set('viewportTopOffset', this.scrollContainerTopOffset);

    this.listenTo(container, 'scroll', throttle(() => {
      // eslint-disable-next-line no-underscore-dangle
      stickyPanel._checkIfShouldBeSticky();
    }, 100));
  },

  initConfig() {
    return Object.assign({}, defaultConfig, this.config);
  },

  beforeCache() {
    if (this.editor) this.editor.destroy();
    this.destroyEditor();
  },
});
